import type { Guest } from './schemas/guest.js';
import type { SeatingConstraint, Table } from './schemas/seating.js';

/**
 * Seating: who sits where, worked out as pure arithmetic.
 *
 * No DOM, no clock, no storage — the canvas drags rectangles around, and this
 * decides whether the result is allowed and what a good starting point would be.
 *
 * The problem is bin-packing with conflicts, which is NP-hard in general and
 * entirely tractable at wedding scale: 150 people, 15 tables, a handful of
 * rules. So this is a deliberate greedy pass rather than a solver, and when it
 * cannot place somebody it says so instead of producing a plan that quietly
 * breaks a rule.
 */

/** Everyone who needs a chair: said yes, or has not said no yet. */
export const needsSeat = (guest: Guest): boolean =>
  guest.rsvp === 'attending' || guest.rsvp === 'maybe' || guest.rsvp === 'pending';

/**
 * A set of people who must share a table.
 *
 * Built from the `together` constraints **and from households**: a family that
 * arrived in one envelope expects to sit together, and making the couple write a
 * constraint for every household would be forty rules to express the obvious.
 * Splitting a household is still possible — by dragging — it just is not what
 * the automatic pass does.
 */
export interface SeatGroup {
  guestIds: string[];
  size: number;
  /** The households represented, for the "keep families together" tie-break. */
  householdIds: string[];
}

class UnionFind {
  private parent = new Map<string, string>();

  find(x: string): string {
    const p = this.parent.get(x);
    if (p === undefined) {
      this.parent.set(x, x);
      return x;
    }
    if (p === x) return x;
    const root = this.find(p);
    this.parent.set(x, root);
    return root;
  }

  union(a: string, b: string): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent.set(ra, rb);
  }
}

export function buildGroups(
  guests: readonly Guest[],
  constraints: readonly SeatingConstraint[],
): SeatGroup[] {
  const seated = guests.filter(needsSeat);
  const uf = new UnionFind();
  for (const g of seated) uf.find(g.guestId);

  // Households first, then explicit constraints on top — a `together` rule can
  // merge two households, which is exactly what it is for.
  const byHousehold = new Map<string, string[]>();
  for (const g of seated) {
    const list = byHousehold.get(g.householdId) ?? [];
    list.push(g.guestId);
    byHousehold.set(g.householdId, list);
  }
  for (const members of byHousehold.values()) {
    for (let i = 1; i < members.length; i += 1)
      uf.union(members[0] as string, members[i] as string);
  }

  const present = new Set(seated.map((g) => g.guestId));
  for (const c of constraints) {
    if (c.kind !== 'together') continue;
    const ids = c.guestIds.filter((id) => present.has(id));
    for (let i = 1; i < ids.length; i += 1) uf.union(ids[0] as string, ids[i] as string);
  }

  const groups = new Map<string, SeatGroup>();
  for (const g of seated) {
    const root = uf.find(g.guestId);
    const group = groups.get(root) ?? { guestIds: [], size: 0, householdIds: [] };
    group.guestIds.push(g.guestId);
    group.size += 1;
    if (!group.householdIds.includes(g.householdId)) group.householdIds.push(g.householdId);
    groups.set(root, group);
  }
  return [...groups.values()];
}

/** Pairs who must not share a table, as a symmetric lookup. */
export function apartPairs(constraints: readonly SeatingConstraint[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const add = (a: string, b: string) => {
    const set = map.get(a) ?? new Set<string>();
    set.add(b);
    map.set(a, set);
  };
  for (const c of constraints) {
    if (c.kind !== 'apart') continue;
    for (const a of c.guestIds) {
      for (const b of c.guestIds) if (a !== b) add(a, b);
    }
  }
  return map;
}

export type ViolationKind =
  'apart_same_table' | 'over_capacity' | 'group_too_large' | 'no_table_fits' | 'unseated';

export interface Violation {
  kind: ViolationKind;
  message: string;
  guestIds: string[];
  tableId?: string;
}

/**
 * What is wrong with a seating plan as it stands.
 *
 * Reported rather than prevented: the canvas lets you drag anybody anywhere,
 * because a couple rearranging tables at 11pm knows things this code does not,
 * and a tool that refuses the drop is a tool they stop using. It warns, loudly,
 * and leaves the decision alone.
 */
export function findViolations(
  guests: readonly Guest[],
  tables: readonly Table[],
  constraints: readonly SeatingConstraint[],
): Violation[] {
  const out: Violation[] = [];
  const apart = apartPairs(constraints);
  const seated = guests.filter(needsSeat);
  const byTable = new Map<string, Guest[]>();

  for (const g of seated) {
    if (!g.tableId) continue;
    const list = byTable.get(g.tableId) ?? [];
    list.push(g);
    byTable.set(g.tableId, list);
  }

  for (const table of tables) {
    const sitting = byTable.get(table.tableId) ?? [];
    if (sitting.length > table.capacity) {
      out.push({
        kind: 'over_capacity',
        message: `${table.name} has ${sitting.length} people and seats ${table.capacity}`,
        guestIds: sitting.map((g) => g.guestId),
        tableId: table.tableId,
      });
    }
    // Each pair once: the apart map is symmetric, so without the index guard
    // every conflict would be reported twice.
    for (let i = 0; i < sitting.length; i += 1) {
      for (let j = i + 1; j < sitting.length; j += 1) {
        const a = sitting[i] as Guest;
        const b = sitting[j] as Guest;
        if (apart.get(a.guestId)?.has(b.guestId)) {
          out.push({
            kind: 'apart_same_table',
            message: `Two people who should be apart are both at ${table.name}`,
            guestIds: [a.guestId, b.guestId],
            tableId: table.tableId,
          });
        }
      }
    }
  }

  const unseated = seated.filter((g) => !g.tableId);
  if (unseated.length > 0) {
    out.push({
      kind: 'unseated',
      message: `${unseated.length} still need a seat`,
      guestIds: unseated.map((g) => g.guestId),
    });
  }
  return out;
}

export interface SeatingPlan {
  /** guestId to tableId. Everyone the pass could place. */
  assignments: { guestId: string; tableId: string }[];
  /** Why the rest could not be placed. */
  unplaced: Violation[];
  /** Seats left, per table, after the pass. */
  remaining: { tableId: string; free: number }[];
}

/**
 * A starting point, not an answer.
 *
 * First-fit decreasing: biggest party first, into the first table that can hold
 * it without breaking an `apart` rule. Largest-first matters — place the couples
 * first and a party of eight has nowhere left to go, which is the classic
 * bin-packing failure and exactly what happens if you seat people in the order
 * the guest list happens to be in.
 *
 * Deterministic: the same input gives the same plan, so pressing the button
 * twice does not reshuffle a room somebody has started arranging by hand.
 */
export function autoSeat(
  guests: readonly Guest[],
  tables: readonly Table[],
  constraints: readonly SeatingConstraint[],
): SeatingPlan {
  const apart = apartPairs(constraints);
  const groups = buildGroups(guests, constraints).sort(
    // Size first, then id, so ties break the same way every run.
    (a, b) => b.size - a.size || (a.guestIds[0] ?? '').localeCompare(b.guestIds[0] ?? ''),
  );

  const free = new Map(tables.map((t) => [t.tableId, t.capacity]));
  const occupants = new Map<string, string[]>(tables.map((t) => [t.tableId, []]));
  const assignments: { guestId: string; tableId: string }[] = [];
  const unplaced: Violation[] = [];

  const largestTable = Math.max(0, ...tables.map((t) => t.capacity));

  for (const group of groups) {
    if (group.size > largestTable) {
      unplaced.push({
        kind: 'group_too_large',
        message:
          tables.length === 0
            ? 'There are no tables yet'
            : `${group.size} people must sit together and the largest table seats ${largestTable}`,
        guestIds: group.guestIds,
      });
      continue;
    }

    const target = tables.find((t) => {
      if ((free.get(t.tableId) ?? 0) < group.size) return false;
      const sitting = occupants.get(t.tableId) ?? [];
      return !group.guestIds.some((id) => sitting.some((other) => apart.get(id)?.has(other)));
    });

    if (!target) {
      unplaced.push({
        kind: 'no_table_fits',
        message: `No table has ${group.size} free seats without breaking a rule`,
        guestIds: group.guestIds,
      });
      continue;
    }

    free.set(target.tableId, (free.get(target.tableId) ?? 0) - group.size);
    occupants.set(target.tableId, [...(occupants.get(target.tableId) ?? []), ...group.guestIds]);
    for (const guestId of group.guestIds) {
      assignments.push({ guestId, tableId: target.tableId });
    }
  }

  return {
    assignments,
    unplaced,
    remaining: tables.map((t) => ({ tableId: t.tableId, free: free.get(t.tableId) ?? 0 })),
  };
}

export interface SeatingStats {
  needSeats: number;
  seated: number;
  unseated: number;
  tables: number;
  capacity: number;
}

export function seatingStats(guests: readonly Guest[], tables: readonly Table[]): SeatingStats {
  const need = guests.filter(needsSeat);
  const seated = need.filter((g) => g.tableId).length;
  return {
    needSeats: need.length,
    seated,
    unseated: need.length - seated,
    tables: tables.length,
    capacity: tables.reduce((sum, t) => sum + t.capacity, 0),
  };
}
